import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { IconButtonProps } from '@ui/components/Button'
import { Modal } from '@ui/components/Modal'
import { FieldGroup } from '@ui/components/Modal/Body/TableFields'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { commissionTiers } from '@utils/MultiTier'
import { useWindowResize } from '@utils/domUtils'

import {
  AssignedCommissionPlan,
  CommissionPlan,
  PlanValues,
  PlanValuesKeys,
  UpdateCommissionPlan,
} from '@model/MultiTierDto'

interface AddNewPlanModalProps {
  summary: PlanValues
  commissionPlan?: AssignedCommissionPlan
  commissionPlans: CommissionPlan[]
  onSubmit(commissionPlans: UpdateCommissionPlan[], commissionPlanId?: string): void
}

const minRebate = 0.01

const useForm = (props: AddNewPlanModalProps & { onClose: () => void }) => {
  const { onSubmit, summary, commissionPlan, commissionPlans, onClose } = props

  const { t } = useTranslation()

  const currentTiers = commissionTiers(commissionPlan?.commissionPlans)
  const [values, setValues] = useState<PlanValues>({ ...currentTiers })

  const getFieldErrors = useCallback(
    (fieldName: keyof PlanValues) => {
      if ((values[fieldName] ?? 0) < (currentTiers[fieldName] ?? 0)) {
        return t('IB.MultiTier.Rebate cannot be decreased')
      }
      if ((values[fieldName] ?? 0) > (summary[fieldName] ?? 0)) {
        return t('IB.MultiTier.Rebate cannot be more than max rebate available')
      }
      if (!!values[fieldName] && values[fieldName]! < minRebate) {
        return t('Validation.Amount cannot be below allowed minimum of', {
          amount: minRebate,
        })
      }
    },
    [currentTiers, summary, t, values]
  )

  const isValid = useMemo(
    () =>
      commissionPlans.every(
        (key) =>
          !!values[key.tradingAccountType.name as PlanValuesKeys]?.toString() &&
          !getFieldErrors(key.tradingAccountType.name as PlanValuesKeys) &&
          (values[key.tradingAccountType.name as PlanValuesKeys] ?? 0) >= 0.01
      ),
    [values, getFieldErrors, commissionPlans]
  )

  const handleSubmitForm = () => {
    const updatedCommissionPlans: UpdateCommissionPlan[] = commissionPlans?.map((plan) => ({
      tradingAccountTypeId: plan.tradingAccountType.id,
      amount: values[plan.tradingAccountType.name as keyof PlanValues] ?? 0,
    }))
    onSubmit(updatedCommissionPlans, commissionPlan?.id)
    onClose()
  }

  return { handleSubmitForm, isValid, getFieldErrors, values, setValues }
}

export const AddEditNewPlanModal: BaseModalType<AddNewPlanModalProps> = (props) => {
  const { close, commissionPlan, commissionPlans } = props
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const formProps = useForm({ ...props, onClose: close })
  const { isValid, handleSubmitForm } = formProps

  const sections: FieldGroup[] = [
    {
      sectionLabel: t('IB.MultiTier.Max Rebate available multiline'),
      fields: commissionPlans.map((plan) => ({
        label: plan.tradingAccountType.name,
        field: {
          size: 'lg',
          type: 'number',
          value: plan.amount,
          disabled: true,
          readOnly: true,
        },
      })),
    },
    {
      sectionLabel: commissionPlan?.name
        ? `IB ${commissionPlan?.name}`
        : t('IB.MultiTier.New Plan'),
      fields: commissionPlans.map((plan) => ({
        label: plan.tradingAccountType.name,
        value: formProps.values[plan.tradingAccountType.name as keyof PlanValues],
        field: {
          size: 'lg',
          value: formProps.values[plan.tradingAccountType.name as keyof PlanValues],
          type: 'number',
          decimalPlaces: 2,
          maxDigits: 2,
          zerosLimitter: true,
          lockOnLimit: true,
          label: '0',
          showLabel: false,
          min: 0.0,
          error: !!formProps.getFieldErrors(plan.tradingAccountType.name as keyof PlanValues),
          hint: formProps.getFieldErrors(plan.tradingAccountType.name as keyof PlanValues),
          onChange: (planAmount) => {
            formProps.setValues((prev) => ({
              ...prev,
              [plan.tradingAccountType.name]: planAmount.target.value,
            }))
          },
        },
      })),
    },
  ]

  const title = commissionPlan ? t('IB.MultiTier.Edit IB Plan') : t('IB.MultiTier.Add new Plan')

  const iconButton = isMobile
    ? ({
        icon: ArrowIcon.Left,
        variant: 'secondary',
        size: 'sm',
        onClick: close,
      } satisfies IconButtonProps)
    : undefined

  return (
    <Modal.FullScreen>
      <Modal.Header canClose={!isMobile} iconButton={iconButton}>
        {title}
      </Modal.Header>

      <Modal.Body>
        <Modal.Body.FieldGroups sections={sections} />
      </Modal.Body>

      <Modal.Footer>
        {isMobile && <Modal.Footer.Secondary onClick={close}>{t('Close')}</Modal.Footer.Secondary>}
        <Modal.Footer.Primary disabled={!isValid} onClick={handleSubmitForm}>
          {t('Submit')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal.FullScreen>
  )
}
